import { type FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { chakra, Link as ExternalLink, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  TeamMemberRole,
  TeamMemberTransitionDisabledReason,
  TeamMemberTransitionRole,
} from '__generated__/GQL';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineTag from 'components/_core/SunshineTag';
import { isFreeRole } from 'features/Team/utils';
import * as upgradeRoutes from 'features/Upgrade/routes';

import locales from './locales';
import { MemberRoleDisabledReasonTag } from './MemberRoleDisabledReasonTag';
import {
  EDITABLE_ROLES,
  type EditableRole,
  type MemberInformationRoleFormSchema,
  memberInformationRoleFormSchema,
} from './schema';

function toRoleTransitionFormRole(
  role: TeamMemberRole,
): TeamMemberTransitionRole | null {
  switch (role) {
    case TeamMemberRole.AccountHolder:
      return TeamMemberTransitionRole.AccountHolder;
    case TeamMemberRole.Employee:
      return TeamMemberTransitionRole.Employee;
    case TeamMemberRole.Admin:
      return TeamMemberTransitionRole.Admin;
    case TeamMemberRole.Accountant:
      return TeamMemberTransitionRole.Accountant;
    default:
      return null;
  }
}

type MemberTransition = {
  disabledReason?: TeamMemberTransitionDisabledReason | null;
  isDisabled: boolean;
  role: TeamMemberTransitionRole;
};

export type MemberInformationFormProps = {
  onSubmit: SubmitHandler<MemberInformationRoleFormSchema>;
  onChange?: (data: EditableRole) => void;
  currentRole?: EditableRole;
  isFreeRolesOnly: boolean;
  showFreeRolesTag: boolean;
  roleTransitions: Map<TeamMemberTransitionRole, MemberTransition>;
  id: string;
};

export const MemberInformationRoleForm: FC<MemberInformationFormProps> = ({
  currentRole,
  id,
  isFreeRolesOnly,
  onChange: onSelectedRoleChanged,
  onSubmit,
  roleTransitions,
  showFreeRolesTag,
}) => {
  const form = useForm<MemberInformationRoleFormSchema>({
    defaultValues: {
      role: currentRole,
    },
    mode: 'onBlur',
    resolver: zodResolver(memberInformationRoleFormSchema),
  });

  const canSelectRole = (
    role: EditableRole,
  ):
    | {
        disabledReason?: TeamMemberTransitionDisabledReason;
        isDisabled: true;
      }
    | {
        isDisabled: false;
      } => {
    const roleTransition = toRoleTransitionFormRole(role);
    if (roleTransition) {
      const roleTransitionConfig = roleTransitions.get(roleTransition);
      if (roleTransitionConfig) {
        if (roleTransitionConfig.isDisabled) {
          return {
            disabledReason: roleTransitionConfig.disabledReason ?? undefined,
            isDisabled: true,
          };
        }
      }
    }

    if (isFreeRole(role)) {
      return {
        isDisabled: false,
      };
    }

    switch (role) {
      case 'ADMIN':
      case 'EMPLOYEE':
        return {
          isDisabled: isFreeRolesOnly,
        };
      default:
        return {
          isDisabled: false,
        };
    }

    return {
      isDisabled: false,
    };
  };

  return (
    <chakra.form
      display="contents"
      id={id}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <VStack gap="space-12">
        <Form.Field width="full">
          <Controller
            control={form.control}
            name="role"
            render={({ field: { onChange, value } }) => (
              <SunshineCard.RadioGroup
                direction="row"
                onChange={(val) => {
                  onChange(val);
                  onSelectedRoleChanged?.(val as EditableRole);
                }}
                value={value}
              >
                {EDITABLE_ROLES.map((role) => {
                  const roleConfig = canSelectRole(role);
                  return (
                    <SunshineCard
                      isDisabled={roleConfig.isDisabled}
                      key={role}
                      value={role}
                      variant="radio"
                    >
                      <SunshineCard.Title>
                        {locales.roles[role].label}
                      </SunshineCard.Title>
                      <SunshineCard.Content gap="space-6">
                        <SunshineCard.Text>
                          {locales.roles[role].description}
                        </SunshineCard.Text>
                        {roleConfig.isDisabled ? (
                          <MemberRoleDisabledReasonTag
                            reason={roleConfig.disabledReason}
                          />
                        ) : null}
                        {showFreeRolesTag && isFreeRole(role) ? (
                          <SunshineCard.Text>
                            <SunshineTag colorScheme="blue" size="sm">
                              {locales.free}
                            </SunshineTag>
                          </SunshineCard.Text>
                        ) : null}
                      </SunshineCard.Content>
                    </SunshineCard>
                  );
                })}
              </SunshineCard.RadioGroup>
            )}
          />
        </Form.Field>
        {isFreeRolesOnly ? (
          <Callout hideIcon>
            <Callout.Content>{locales.proBusinessOnly}</Callout.Content>
            <Callout.Content>
              <Link to={upgradeRoutes.root}>
                <Button variant="inline-secondary">
                  {locales.upgradePlan}
                </Button>
              </Link>
            </Callout.Content>
          </Callout>
        ) : (
          <Callout hideIcon>
            <Callout.Title>{locales.roleSelectionHelp.title}</Callout.Title>
            <Callout.Content>
              {locales.roleSelectionHelp.description}{' '}
              <ExternalLink
                href="https://help.shine.fr/fr/articles/6426418-roles-et-acces-au-compte"
                isExternal
              >
                {locales.roleSelectionHelp.descriptionLink}

                <SunshineIcon name="link" />
              </ExternalLink>
              .
            </Callout.Content>
          </Callout>
        )}
      </VStack>
    </chakra.form>
  );
};
